import React, { useMemo, useState } from "react";
import { getEfadaStudent, saveEfadaFile } from "../../actions/studensts";
import AlertSuccess from "../../components/AlertSuccess";
import AlertDenger from "../../components/AlertDenger";
import * as FaIcons from "react-icons/fa";
import UNAUTHORIZED from "../UNAUTHORIZED";
import { STATEMENT_USERS } from "./EfadaHistory";
import StatementHeader from "./StatementHeader";
import {
  generateEfadaDocument,
  generateArabicEfadaDocument,
  generateMoheStatementDocument,
  sessionText,
  todayParts,
} from "../../utils/efadaDocument";

// ACT 1 composite for the Arabic letter ("إجمالى درجة 22 من 36"): the ticked
// grades' trial composite, or the best one if grades from several trials are
// ticked.
const compositeOf = (grades) => {
  const totals = grades.map((g) => Number(g.total_score)).filter((n) => n > 0);
  return totals.length ? String(Math.max(...totals)) : "";
};

const inputClass =
  "border border-gray-300 rounded-lg p-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400";
const labelClass = "block text-xs font-semibold text-gray-600 mb-1";

// A white section card with a title row (and optional right-side content).
function Card({ title, icon: Icon, aside, children, className = "" }) {
  return (
    <section className={`bg-white rounded-xl shadow-lg ${className}`}>
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100">
        <h2 className="flex items-center gap-2 text-base font-bold text-gray-800">
          {Icon && <Icon className="text-blue-800" />}
          {title}
        </h2>
        {aside}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

// One exam's grade picker: a checkbox per stored grade plus the session wording
// ("بدور …") that goes into the letters. The session text follows the ticked
// grades' dates until the user edits it by hand.
function ExamSection({
  label,
  grades,
  selected,
  onToggle,
  onToggleAll,
  session,
  onSessionChange,
  showTrial,
  children,
}) {
  if (!grades.length) {
    return (
      <Card title={label} icon={FaIcons.FaClipboardList}>
        <p className="text-sm text-gray-500 py-6 text-center">
          No {label} grades recorded for this student.
        </p>
      </Card>
    );
  }

  const tickedCount = grades.filter((g) => selected.has(g.key)).length;
  const allTicked = tickedCount === grades.length;
  const th = "px-3 py-2 font-semibold";

  return (
    <Card
      title={label}
      icon={FaIcons.FaClipboardList}
      aside={
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-gray-500">
            {tickedCount} of {grades.length} selected
          </span>
          <button
            type="button"
            onClick={() => onToggleAll(!allTicked)}
            className="text-xs font-semibold text-blue-800 hover:underline"
          >
            {allTicked ? "Clear all" : "Select all"}
          </button>
        </div>
      }
    >
      <div className="overflow-x-auto rounded-lg border border-gray-200 mb-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className={`${th} w-10`}></th>
              {showTrial && <th className={th}>Trial</th>}
              <th className={th}>Subject</th>
              <th className={th}>Score</th>
              {showTrial && <th className={th}>Composite</th>}
              <th className={th}>Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {grades.map((g) => (
              <tr
                key={g.key}
                onClick={() => onToggle(g.key)}
                className={`cursor-pointer transition-colors ${
                  selected.has(g.key) ? "bg-blue-50" : "hover:bg-gray-50"
                }`}
              >
                <td className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={selected.has(g.key)}
                    onChange={() => onToggle(g.key)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                {showTrial && <td className="px-3 py-2 text-gray-600">{g.trial_no}</td>}
                <td className="px-3 py-2 font-medium text-gray-800">{g.subject}</td>
                <td className="px-3 py-2 font-bold text-gray-900">{g.score}</td>
                {showTrial && <td className="px-3 py-2 text-gray-600">{g.total_score ?? "—"}</td>}
                <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                  {g.date ? String(g.date).slice(0, 10) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <label className={labelClass}>Session (بدور …)</label>
      <input
        dir="rtl"
        type="text"
        value={session}
        onChange={(e) => onSessionChange(e.target.value)}
        placeholder="e.g. يوليو 2024"
        className={inputClass}
      />
      {children}
    </Card>
  );
}

// The three letters, each a button with a short line on what it is.
// `hidden: true` removes the button but keeps its generator, so it can be
// switched back on by deleting the flag.
const LETTERS = [
  { key: "efada", title: "الافادة", note: "Exam administration to the Ministry", color: "bg-green-600 hover:bg-green-700", hidden: true },
  { key: "arabic", title: "افادة باللغة العربية", note: "ACT representative letter", color: "bg-blue-800 hover:bg-blue-700", hidden: true },
  { key: "mohe", title: "MOHE Statement", note: "For MOHE coordination", color: "bg-purple-700 hover:bg-purple-600" },
];

export default function Efada() {
  const [nationalId, setNationalId] = useState("");
  const [loading, setLoading] = useState(false);
  // false, or which letter is being generated ("efada" / "arabic" / "mohe").
  const [generating, setGenerating] = useState(false);
  const [data, setData] = useState(null);

  const [studentName, setStudentName] = useState("");
  const [gender, setGender] = useState("female");
  const [selected, setSelected] = useState(new Set());
  // null = follow the ticked grades; a string = typed by the user.
  const [sessionEdits, setSessionEdits] = useState({ act1: null, act2: null });
  const [compositeEdit, setCompositeEdit] = useState(null);
  // MOHE statement only: "بشهادة صادرة وموثقة بتاريخ …" (YYYY-MM-DD).
  const [certificateDate, setCertificateDate] = useState("");

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const flashSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };
  const flashError = (msg) => {
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(""), 3500);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const id = nationalId.trim();
    if (!id) return;
    setLoading(true);
    const res = await getEfadaStudent(id);
    setLoading(false);
    if (res && res.status === 200) {
      const d = res.data;
      setData(d);
      setStudentName(d.student.name_in_arabic || d.student.full_name || "");
      setGender(d.student.gender === "male" ? "male" : "female");
      // Start with every grade ticked; untick what shouldn't be quoted.
      setSelected(new Set([...d.act1, ...d.act2].map((g) => g.key)));
      setSessionEdits({ act1: null, act2: null });
      setCompositeEdit(null);
      setCertificateDate(d.student.authentication_date || "");
    } else {
      setData(null);
      flashError(res?.data?.ERROR || "Student not found.");
    }
  };

  // Changing the ticked grades re-derives that exam's session (and the ACT 1
  // composite) from the new selection.
  const resetDerived = (exam) => {
    setSessionEdits((prev) => ({ ...prev, [exam]: null }));
    if (exam === "act1") setCompositeEdit(null);
  };

  const toggleGrade = (exam) => (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    resetDerived(exam);
  };

  const toggleAll = (exam, on) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const g of data[exam]) {
        if (on) next.add(g.key);
        else next.delete(g.key);
      }
      return next;
    });
    resetDerived(exam);
  };

  const picked = useMemo(() => {
    if (!data) return { act1: [], act2: [] };
    return {
      act1: data.act1.filter((g) => selected.has(g.key)),
      act2: data.act2.filter((g) => selected.has(g.key)),
    };
  }, [data, selected]);

  const sessions = {
    act1: sessionEdits.act1 ?? sessionText(picked.act1.map((g) => g.date)),
    act2: sessionEdits.act2 ?? sessionText(picked.act2.map((g) => g.date)),
  };
  const composite = compositeEdit ?? compositeOf(picked.act1);
  const hasPicked = picked.act1.length > 0 || picked.act2.length > 0;

  const { day, month, year } = todayParts();

  // Checks shared by all letters; returns an error message or null.
  const validate = () => {
    if (!hasPicked) return "Select at least one grade.";
    if (!studentName.trim()) return "Student name is required.";
    if (picked.act1.length && !sessions.act1.trim()) return "Enter the ACT 1 session.";
    if (picked.act2.length && !sessions.act2.trim()) return "Enter the ACT 2 session.";
    return null;
  };

  const runGenerate = async (which, build) => {
    if (generating) return;
    const error = validate();
    if (error) return flashError(error);
    setGenerating(which);
    try {
      const { blob, fileName } = await build();
      // The letter is already downloaded; a failed history upload only warns.
      const saved = await saveEfadaFile({
        blob,
        fileName,
        letterType: which,
        nationalId: data.student.id,
        studentName: studentName.trim(),
      });
      if (saved && saved.status === 201) flashSuccess("Document generated.");
      else flashError("Document downloaded, but saving a copy to Efada History failed.");
    } catch (err) {
      flashError(err.message || "Failed to generate the document.");
    }
    setGenerating(false);
  };

  const handleGenerate = () =>
    runGenerate("efada", () =>
      generateEfadaDocument({
        studentName: studentName.trim(),
        nationalId: data.student.id,
        gender,
        exams: pickedExams(),
      })
    );

  // Exams with ticked grades, in letter order (ACT 1 first).
  const pickedExams = () =>
    [
      { key: "act1", label: "ACT 1" },
      { key: "act2", label: "ACT 2" },
    ]
      .filter((e) => picked[e.key].length)
      .map((e) => ({
        label: e.label,
        grades: picked[e.key],
        session: sessions[e.key].trim(),
      }));

  const handleGenerateMohe = () => {
    if (!certificateDate) return flashError("Enter the certificate date.");
    return runGenerate("mohe", () =>
      generateMoheStatementDocument({
        studentName: studentName.trim(),
        nationalId: data.student.id,
        gender,
        exams: pickedExams(),
        certificateDate,
      })
    );
  };

  const handleGenerateArabic = () => {
    if (picked.act1.length && !String(composite).trim()) {
      return flashError("Enter the ACT 1 composite score.");
    }
    return runGenerate("arabic", () =>
      generateArabicEfadaDocument({
        studentName: studentName.trim(),
        nationalId: data.student.id,
        gender,
        act1: picked.act1.length
          ? {
              composite: String(composite).trim(),
              session: sessions.act1.trim(),
              grades: picked.act1,
            }
          : null,
        act2: picked.act2.length
          ? { session: sessions.act2.trim(), grades: picked.act2 }
          : null,
      })
    );
  };

  const username = JSON.parse(localStorage.getItem("profile"))?.value?.[0]?.username;
  if (!STATEMENT_USERS.includes(username)) return <UNAUTHORIZED />;

  const generators = { efada: handleGenerate, arabic: handleGenerateArabic, mohe: handleGenerateMohe };

  return (
    <div className="px-4 pb-10 max-w-7xl mx-auto">
      {successMsg && <AlertSuccess text={successMsg} />}
      {errorMsg && <AlertDenger text={errorMsg} />}

      <StatementHeader
        icon={FaIcons.FaFileSignature}
        title="الافادة (Statement)"
        subtitle="Find a student, choose which ACT grades to quote, and download a letter as a Word document. All letters are dated today."
        link={{ to: "/main/efada_history", label: "Statement History", icon: FaIcons.FaHistory }}
      />

      <form onSubmit={handleSearch} className="mb-5">
        <Card title="Find student" icon={FaIcons.FaSearch}>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <label className={labelClass}>National ID</label>
              <input
                type="text"
                className={inputClass}
                value={nationalId}
                onChange={(e) => setNationalId(e.target.value)}
                placeholder="e.g. 30711022102727"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !nationalId.trim()}
              className="inline-flex items-center gap-2 px-5 py-2 bg-blue-800 text-white rounded-lg text-sm font-medium
                         hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <FaIcons.FaSearch />
              {loading ? "Loading..." : "Find Student"}
            </button>
          </div>
        </Card>
      </form>

      {data && (
        <>
          <Card
            title="Student details"
            icon={FaIcons.FaUserGraduate}
            className="mb-5"
            aside={
              <span className="text-xs font-medium text-gray-500 truncate">
                {data.student.full_name} · {data.student.id}
              </span>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div>
                <label className={labelClass}>Student name (as written in the letters)</label>
                <input
                  dir="rtl"
                  type="text"
                  className={inputClass}
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                />
                {!data.student.name_in_arabic && (
                  <p className="text-xs text-amber-600 mt-1">
                    No Arabic name on file — the English name was filled in.
                  </p>
                )}
              </div>
              <div>
                <label className={labelClass}>Gender (الطالب / الطالبة)</label>
                <select
                  className={inputClass}
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                >
                  <option value="female">Female — الطالبة</option>
                  <option value="male">Male — الطالب</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Letter date</label>
                <p className="p-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg">
                  Today — {day}/{month}/{year}
                </p>
              </div>
              <div>
                <label className={labelClass}>
                  Certificate date (صادرة وموثقة بتاريخ) — MOHE only
                </label>
                <input
                  type="date"
                  className={inputClass}
                  value={certificateDate}
                  onChange={(e) => setCertificateDate(e.target.value)}
                />
                {!data.student.authentication_date && (
                  <p className="text-xs text-amber-600 mt-1">
                    No authentication date on file — enter it by hand.
                  </p>
                )}
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            <ExamSection
              label="ACT 1"
              showTrial
              grades={data.act1}
              selected={selected}
              onToggle={toggleGrade("act1")}
              onToggleAll={(on) => toggleAll("act1", on)}
              session={sessions.act1}
              onSessionChange={(v) => setSessionEdits((p) => ({ ...p, act1: v }))}
            >
              <label className={`${labelClass} mt-4`}>
                Composite out of 36 (إجمالى درجة) — Arabic letter only
              </label>
              <input
                type="number"
                min="1"
                max="36"
                value={composite}
                onChange={(e) => setCompositeEdit(e.target.value)}
                className="border border-gray-300 rounded-lg p-2 text-sm w-32
                           focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </ExamSection>
            <ExamSection
              label="ACT 2"
              grades={data.act2}
              selected={selected}
              onToggle={toggleGrade("act2")}
              onToggleAll={(on) => toggleAll("act2", on)}
              session={sessions.act2}
              onSessionChange={(v) => setSessionEdits((p) => ({ ...p, act2: v }))}
            />
          </div>

          <Card
            title="Generate letter"
            icon={FaIcons.FaFileWord}
            aside={
              !hasPicked && (
                <span className="text-xs font-medium text-amber-600">Select at least one grade</span>
              )
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {LETTERS.filter((l) => !l.hidden).map((l) => (
                <button
                  key={l.key}
                  type="button"
                  onClick={generators[l.key]}
                  disabled={Boolean(generating) || !hasPicked}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-white text-left transition-colors
                              disabled:bg-gray-400 disabled:cursor-not-allowed ${l.color}`}
                >
                  <FaIcons.FaFileWord className="text-2xl shrink-0 opacity-90" />
                  <span className="min-w-0">
                    <span className="block font-bold">
                      {generating === l.key ? "Generating..." : l.title}
                    </span>
                    <span className="block text-xs opacity-80">{l.note}</span>
                  </span>
                </button>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
